import { useState, useMemo } from 'react';

// Click-to-sort for dashboard tables (added 19 Oct 2026, Candidates + Participants).
//
//   const { sorted, sort, toggle } = useSortable(rows, SORTERS, { key: 'added', dir: 'desc' });
//   <SortHeader label="Name" sortKey="name" sort={sort} onSort={toggle} />
//
// SORTERS maps a column key to { value: row => what to sort by, firstDir }.
// Clicking a new column sorts by it (firstDir, default 'asc'); clicking the same
// column again reverses it. Blanks always go to the bottom, whichever direction.
// Sorting happens in the browser and resets when the page is left.
export default function useSortable(rows, sorters, initial) {
  const [sort, setSort] = useState(initial);

  const sorted = useMemo(() => {
    const sorter = sorters[sort.key];
    if (!sorter) return rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    const blank = v => v === null || v === undefined || v === '';
    return [...rows].sort((a, b) => {
      const va = sorter.value(a);
      const vb = sorter.value(b);
      if (blank(va) || blank(vb)) return blank(va) - blank(vb);
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return cmp * sign;
    });
  }, [rows, sorters, sort]);

  const toggle = key => setSort(prev => (prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: sorters[key]?.firstDir || 'asc' }));

  return { sorted, sort, toggle };
}

// A table header cell that sorts its column when clicked and shows ▲ / ▼.
export function SortHeader({ label, sortKey, sort, onSort, className = '' }) {
  const active = sort.key === sortKey;
  return (
    <th className={className} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase hover:text-carriera-accent transition-colors duration-200 ${active ? 'text-carriera-text' : ''}`}>
        {label}
        <span className={`text-[9px] ${active ? 'text-carriera-accent' : 'opacity-30'}`}>
          {active && sort.dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}
